import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Icon from "@material-ui/core/Icon";

import { useAuth } from "context/AuthContext";
import api from "api/index";

const NAVY = "#1a237e";
const NAVY_DARK = "#0d1b6e";
const NAVY_LIGHT = "#1565c0";

const styles = {
  page: {
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    background: `linear-gradient(150deg, ${NAVY_DARK} 0%, ${NAVY} 50%, ${NAVY_LIGHT} 100%)`,
    padding: "24px", fontFamily: "'Roboto', sans-serif"
  },
  card: {
    width: "100%", maxWidth: "400px", background: "#fff", borderRadius: "16px",
    boxShadow: "0 24px 60px rgba(0,0,0,0.28)", overflow: "hidden"
  },
  topBar: { background: NAVY, padding: "24px 32px", color: "#fff" },
  schoolName: { margin: 0, fontSize: "16px", fontWeight: 700 },
  schoolSub: { margin: "4px 0 0", fontSize: "12px", color: "rgba(255,255,255,0.7)" },
  body: { padding: "28px 32px" },
  heading: { margin: "0 0 4px", fontSize: "17px", fontWeight: 700, color: "#0f172a" },
  subHeading: { margin: "0 0 20px", fontSize: "13px", color: "#94a3b8" },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px",
    padding: "10px 14px", marginBottom: "16px", color: "#b91c1c", fontSize: "13px"
  },
  fieldGroup: { marginBottom: "16px" },
  label: { display: "block", fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px" },
  input: {
    width: "100%", height: "44px", padding: "0 14px", border: "1.5px solid #e2e8f0",
    borderRadius: "8px", fontSize: "14px", boxSizing: "border-box", fontFamily: "inherit"
  },
  submitBtn: {
    width: "100%", height: "46px", border: "none", borderRadius: "8px",
    background: NAVY, color: "#fff", fontSize: "14px", fontWeight: 500,
    textTransform: "none", cursor: "pointer", boxShadow: "none",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
    transition: "background-color 0.15s ease",
    "&:hover,&:focus": { background: NAVY_DARK },
    "&:focus-visible": { outline: `2px solid ${NAVY_LIGHT}`, outlineOffset: "2px" },
    "&:disabled": { opacity: 0.65, cursor: "not-allowed" }
  },
  footer: { textAlign: "center", padding: "0 32px 22px", fontSize: "11px", color: "#94a3b8" }
};

class PortalLoginInner extends Component {
  state = { email: "", password: "", loading: false, error: "" };

  componentDidMount() {
    if (this.props.auth.user?.role === "Student") this.props.history.push("/portal/dashboard");
  }

  handleSubmit = async e => {
    e.preventDefault();
    const { email, password } = this.state;
    if (!email || !password) { this.setState({ error: "Email and password are required." }); return; }
    this.setState({ loading: true, error: "" });
    try {
      const res = await api.post("/auth/login", { email, password });
      const { token, user } = res.data.data || res.data;
      if (user.role !== "Student") {
        this.setState({ error: "This isn't a student account. Please use the staff login.", loading: false });
        return;
      }
      this.props.auth.login(token, user);
      this.props.history.push("/portal/dashboard");
    } catch (err) {
      this.setState({ error: err.response?.data?.message || "Invalid credentials. Please try again.", loading: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { email, password, loading, error } = this.state;

    return (
      <div className={classes.page}>
        <div className={classes.card}>
          <div className={classes.topBar}>
            <p className={classes.schoolName}>Bishop Willis Core PTC</p>
            <p className={classes.schoolSub}>Student Portal</p>
          </div>
          <div className={classes.body}>
            <h2 className={classes.heading}>Sign in</h2>
            <p className={classes.subHeading}>Use your student email and password.</p>

            {error && <div className={classes.errorBox}>{error}</div>}

            <form onSubmit={this.handleSubmit} noValidate>
              <div className={classes.fieldGroup}>
                <label className={classes.label} htmlFor="portal-email">Email</label>
                <input
                  id="portal-email" type="email" autoComplete="email" autoFocus
                  className={classes.input} value={email} disabled={loading}
                  onChange={e => this.setState({ email: e.target.value, error: "" })}
                />
              </div>
              <div className={classes.fieldGroup}>
                <label className={classes.label} htmlFor="portal-password">Password</label>
                <input
                  id="portal-password" type="password" autoComplete="current-password"
                  className={classes.input} value={password} disabled={loading}
                  onChange={e => this.setState({ password: e.target.value, error: "" })}
                />
              </div>
              <button type="submit" className={classes.submitBtn} disabled={loading}>
                {loading ? <CircularProgress size={18} style={{ color: "#fff" }} /> : <><Icon style={{ fontSize: "18px" }}>login</Icon> Sign In</>}
              </button>
            </form>
          </div>
          <div className={classes.footer}>&copy; {new Date().getFullYear()} Bishop Willis Core PTC</div>
        </div>
      </div>
    );
  }
}

PortalLoginInner.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired, auth: PropTypes.object.isRequired };

const PortalLoginStyled = withStyles(styles)(PortalLoginInner);

function PortalLogin(props) {
  const auth = useAuth();
  return <PortalLoginStyled {...props} auth={auth} />;
}

export default withRouter(PortalLogin);
